import { Button, CircularProgress, Collapse, Container, Dialog, DialogContent, DialogTitle, Grid, IconButton, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Tooltip } from "@mui/material"
import { Show, SimpleShowLayout, useNotify, useShowContext, useTranslate } from "react-admin"
import { CardCommon } from "src/components/uiElements"
import { ShowListElement } from "src/components/uiElements/CRUD/ShowListElement"
import { ItemsTable } from "../logic/ItemsTable"
import { useEffect, useState } from "react"
import { itemSupplierServices } from "src/services/purchases/item_supplier/ItemSupplier.service"
import { numberFormatterWithDecimalAndThousand } from "src/helper/numberFormatterWithDecimalAndThousand"
import { parseDateFormat } from "src/helper/parseDateFormat"
import { parseDateOnlyFormat } from "src/helper/parseDateOnlyFormat"
import { ConfirmationDialog } from "src/components/uiElements/styledComponents/ConfirmationDialog"
import { KeyboardArrowDown, KeyboardArrowUp } from "@mui/icons-material"
import { getField } from "../../sales/reservation/logic/payment/helper/getField"
import PersonIcon from '@mui/icons-material/Person';
import { HasPermission } from "src/helper/HasPermission"
import { UserInfoModal } from "./logic/UserInfoModal"

const paymentDetailsByType = {
  payment_cash: [
    'cash_name',
    'client_name',
    'client_document_type',
    'client_document_number',
  ],
  payment_bank: ['bank_name', 'coupon_number', 'date_time', 'amount', 'currency'],
  payment_online: [
    'vendor_code',
    'card_number',
    'quota',
    'bank_name',
    'expiry_date',
    'transaction',
    'agency_auth_code',
  ],
  payment_paypal: ['paypal_id', 'status_detail', 'payer.name.surname', 'amount', 'currency', 'date_time'],
  payment_braspag: [
    'card_number',
    'authorization_code',
    'installments',
    'card_holder',
    'card_brand_name',
    'customer_street',
    'customer_number',
    'customer_city',
    'customer_state',
    'customer_country',
    'customer_district',
    'customer_complement',
    'customer_zip_code',
  ],
  payment_pix: [
    'customer_document_type',
    'customer_document_value',
    'customer_name',
    'tx_id',
    'order_id',
    'date_time',
  ],
  payment_retention: [
    'type',
    'date_time',
    'description',
    'number',
  ],
}

const ShowWrapper = ({ children }) => <Container maxWidth='false'> {children}</Container>

const ShowContent = ({hasEdit = true}) => {
  const {
    error, // error returned by dataProvider when it failed to fetch the record. Useful if you want to adapt the view instead of just showing a notification using the `onError` side effect.
    isLoading, // boolean that is true until the record is available for the first time
    record, // record fetched via dataProvider.getOne() based on the id from the location
    refetch
  } = useShowContext()
  const notify = useNotify()
  const translate = useTranslate()
  const [selectedItems, setSelectedItems] = useState([])
  const [itemsToSelect, setItemsToSelect] = useState([])
  const [payments, setPayments] = useState([])
  const [deleteConfirmation, setDeleteConfirmation] = useState(false)
  const [onConfirmDelete, setOnConfirmDelete] = useState(false)
  const [loadingDelete, setLoadingDelete] = useState(false)
  const [openRows, setOpenRows] = useState([])
  const [showUserModal, setShowUSerModal] = useState(false)
  const [selectedPaymentId, setSelectedPaymentId] = useState(null)

  useEffect(()=>{
    if (record) {
      const initialitems = record?.supplier_payment_order_item_relation?.map(item=>{return {...item.item_supplier_relation, supplier_name:record?.supplier_relation.name, amount_pending:item.item_supplier_relation?.amount_pending, amount:item.amount}})
      setSelectedItems(initialitems)
      setItemsToSelect(initialitems)
      setPayments(record.supplier_payment_order_detail_relation ?? [])
    }
  },[record])

  useEffect(()=>{
    if (selectedPaymentId) {
      setShowUSerModal(true)
    } else {
      setShowUSerModal(false)
    }
  }, [selectedPaymentId])

  useEffect(()=>{
    if (onConfirmDelete) {
      deletePayments()
    }
  }, [onConfirmDelete])

  const deletePayments = async () =>{
    setLoadingDelete(true)
    await itemSupplierServices.deletePayments(record.id).then(()=>{
      notify('ra.notification.delete_paymente_success', {type:'success'})
      refetch()
    }).catch(()=>{
      notify('ra.notification.delete_paymente_error', {type:'error'})
    }).finally(()=>{
      setLoadingDelete(false)
      setDeleteConfirmation(false)
      setOnConfirmDelete(false)
    })
  }

  if (isLoading) {
    return <div>{translate('ra.action.loading')}</div>
  }

  if (error) {
    return <div>{translate('ra.notification.http_error')}</div>
  }

  const onExpandRow = (id) => {
    let openRowsAux = [...openRows]
    const isExpanded = openRowsAux.find((elem) => elem === id)
    if (!isExpanded) {
      openRowsAux.push(id)
    } else {
      openRowsAux = openRowsAux.filter((elem) => elem !== id)
    }
    setOpenRows(openRowsAux)
  }

  return (
    <SimpleShowLayout>
      <CardCommon title={translate('ra.single_entity.supplier-payment-order')}>
        <Grid container spacing={5}>
          <ShowListElement label={translate('ra.field.supplier')} value={record?.supplier_relation?.name} />
          <ShowListElement label={translate('ra.field.currency')} value={record?.currency_relation?.short_name} />
          <ShowListElement label={translate('ra.field.due_date')} includeTime={false} value={record?.due_date ? parseDateOnlyFormat(record?.due_date) : null} />
          <ShowListElement label={translate('ra.field.created_at')} value={parseDateFormat(record.created_at)} />
          <ShowListElement label={translate('ra.field.observations')} value={record.observations} />
        </Grid>
      </CardCommon>

      <CardCommon title={translate('ra.title.list_item_to_pay')} >
        <ItemsTable title={translate('ra.title.item_selected')} isShow enableFilters={false} supplierId={record?.supplier_id} selectedItems={itemsToSelect} setSelectedItems={setItemsToSelect} editData={selectedItems} />
      </CardCommon>

      {hasEdit && (
        <CardCommon title={translate('ra.field.payments')} >
          <TableContainer>
            <Table>
                <TableHead>
                  <TableRow>
                    <TableCell></TableCell>
                    <TableCell>{translate('ra.field.id')}</TableCell>
                    <TableCell>{translate('ra.field.date')}</TableCell>
                    <TableCell>{translate('ra.field.payment_type')}</TableCell>
                    <TableCell>{translate('ra.field.currency')}</TableCell>
                    <TableCell>{translate('ra.field.amount')}</TableCell>
                    <TableCell>{translate('ra.field.change_type')}</TableCell>
                    <TableCell>{translate('ra.field.change_type_reversed')}</TableCell>
                    <TableCell>{translate('ra.title.actions')}</TableCell>
                  </TableRow>
              </TableHead>
              <TableBody>
                {payments?.map((row,i) => {
                    const isExpanded = openRows.includes(row.id)
                    const fields = paymentDetailsByType[row?.payment_entity_relation?.ref_table_detail] || []
                    return (
                      <>
                        <TableRow key={i}>
                          <TableCell style={{ padding: '1px 16px', maxWidth: 50 }}>
                            {fields.length > 0 ? (
                              <IconButton size='small' onClick={() => onExpandRow(row.id)}>
                                {isExpanded ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
                              </IconButton>
                            ) : null}
                          </TableCell>
                          <TableCell>{row?.payment_entity_relation?.id}</TableCell>
                          <TableCell>
                            {row?.payment_entity_relation?.payment_detail_relation?.date_time
                              ? new Date(
                                row?.payment_entity_relation?.payment_detail_relation?.date_time
                              ).toLocaleDateString()
                              : ''}
                          </TableCell>
                          <TableCell>{row?.payment_entity_relation?.type}</TableCell>
                          <TableCell>
                            {row?.payment_entity_relation?.payment_detail_relation?.currency}
                          </TableCell>
                          <TableCell>
                            {numberFormatterWithDecimalAndThousand(
                              row?.payment_entity_relation?.payment_detail_relation?.amount
                            )}
                          </TableCell>
                          <TableCell>{numberFormatterWithDecimalAndThousand(row?.payment_entity_relation?.currencies_exchange?.[0]?.value)}</TableCell>
                          <TableCell>{numberFormatterWithDecimalAndThousand(1/row?.payment_entity_relation?.currencies_exchange?.[0]?.value)}</TableCell>
                          <HasPermission resource={'payment-entity'} permission="revision">
                            <TableCell>
                              <Tooltip title={translate('ra.title.user_information')}>
                                <PersonIcon
                                  style={{alignSelf:'center', width:'30px', cursor:'pointer'}}
                                  onClick={()=>
                                    setSelectedPaymentId(row.payment_entity_id)
                                  }
                                />
                              </Tooltip>
                            </TableCell>
                          </HasPermission>
                        </TableRow>
                        <TableRow key={i}>
                          <TableCell className='collapsible-row' colSpan={8}> {/* falta agrandar espacio entre valores de columnas */}
                            <Collapse in={isExpanded} unmountOnExit >
                            <div style={{display:'flex', flexDirection:'row'}}>
                              {/* <div style={{ display: 'flex', flexDirection: 'column' }} className='field'>
                                  <b>{translate('ra.field.id')}</b>
                                  <span>{row?.payment_entity_relation?.id}</span>
                              </div> */}
                              <div style={{ display: 'flex', flexDirection: 'column', marginRight:'20px' }} className='field'>
                                  <b>{translate('ra.field.date')}</b>
                                  <span>{parseDateFormat(row?.payment_entity_relation?.payment_detail_relation?.date_time)}</span>
                              </div>
                              {/* <div style={{ display: 'flex', flexDirection: 'column' }} className='field'>
                                  <b>{translate('ra.field.payment_type')}</b>
                                  <span>{row?.payment_entity_relation?.payment_detail_relation?.type}</span>
                              </div> */}
                              {/* <div style={{ display: 'flex', flexDirection: 'column' }} className='field'>
                                  <b>{translate('ra.field.currency')}</b>
                                  <span>{row?.payment_entity_relation?.payment_detail_relation?.currency}</span>
                              </div>
                              <div style={{ display: 'flex', flexDirection: 'column' }} className='field'>
                                  <b>{translate('ra.field.amount')}</b>
                                  <span>{numberFormatterWithDecimalAndThousand(row?.payment_entity_relation?.payment_detail_relation?.amount)}</span>
                              </div> */}
                              {/* <div style={{ display: 'flex', flexDirection: 'column' }} className='field'>
                                  <b>{translate('ra.title.exchange_type')}</b>
                                  <span>{row?.payment_entity_relation?.currencies_exchange?.[0]?.value}</span>
                              </div>
                              <div style={{ display: 'flex', flexDirection: 'column' }} className='field'>
                                  <b>{translate('ra.field.change_type_reversed')}</b>
                                  <span>{numberFormatterWithDecimalAndThousand(1/row?.payment_entity_relation?.currencies_exchange?.[0]?.value, 4)}</span>
                              </div> */}
                              {fields.map((field, j) => (
                                <div key={j} style={{ display: 'flex', flexDirection: 'column', marginRight:'20px' }} className='field'>
                                {(field === 'vendor_code') ?
                                <>
                                  (
                                      <b>{row?.payment_entity_relation?.type}</b>
                                      <span>{row?.payment_entity_relation?.payment_detail_relation?.[field]}</span>
                                  )
                                </>
                                :
                                getField(
                                  j,
                                  row?.payment_entity_relation?.payment_detail_relation,
                                  field,
                                  translate
                                )}
                              </div>)
                              )}
                              </div>
                            </Collapse>
                          </TableCell>
                        </TableRow>
                      </>
                  )
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </CardCommon>
      )}

      {payments.length > 0 && (
        <Button
          variant="contained"
          style={{marginTop:'15px'}}
          onClick={()=>setDeleteConfirmation(true)}
        >
          {translate('ra.action.delete_payments')}
        </Button>
      )}

      <ConfirmationDialog
        title={translate('ra.message.delete_payments_message')}
        buttonConfirmText={translate('ra.action.to_confirm')}
        buttonCancel={translate('ra.action.cancel')}
        setOpen={setDeleteConfirmation}
        loadingModalButton={loadingDelete}
        setConfirm={setOnConfirmDelete}
        open={deleteConfirmation}
      />

      <Dialog open={showUserModal} onClose={() => setSelectedPaymentId(null)} fullWidth>
        <DialogTitle>
          {translate('ra.title.user_information')}
        </DialogTitle>
        <DialogContent>
          <UserInfoModal paymentId = {selectedPaymentId} setSelectedPaymentId={setSelectedPaymentId}/>
        </DialogContent>
      </Dialog>
    </SimpleShowLayout>
  )
}

export const SupplierPaymentOrderShow = (props) => {

  return (
    <Show component={ShowWrapper} {...props} actions={false} className='CrudShowContainer'>
      <ShowContent {...props}/>
    </Show>
  )
}